// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import clsx from 'clsx';
import { useMergeRefs, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { InternalButton } from '../button/internal';
import { useInternalI18n } from '../i18n/context';
import { getBaseProps } from '../internal/base-component';
import { fireNonCancelableEvent } from '../internal/events';
import { useFocusRestore } from './use-focus-restore';
import styles from './styles.css.js';
import testStyles from './test-classes/styles.css.js';
export default function InternalDialog({ header, children, footer, headerActions, i18nStrings, onDismiss, __internalRootRef, ...restProps }) {
    const baseProps = getBaseProps(restProps);
    const i18n = useInternalI18n('dialog');
    const dismissAriaLabel = i18n('i18nStrings.dismissAriaLabel', i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.dismissAriaLabel);
    const headerId = useUniqueId('dialog-header-');
    // Move focus into the dialog when it mounts and restore it to the previously focused element when it unmounts.
    // Moving dialog between render branches (for example, swapping containers on a media breakpoint) remounts it and
    // re-triggers this focus. Consumers should keep the same mounted instance when repositioning to avoid the focus from changing.
    const focusRestoreRef = useFocusRestore();
    const mergedRootRef = useMergeRefs(focusRestoreRef, __internalRootRef);
    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            fireNonCancelableEvent(onDismiss);
        }
    };
    const dismissButton = (React.createElement(InternalButton, { className: clsx(styles['dismiss-button'], testStyles['dismiss-button']), variant: "icon", iconName: "close", formAction: "none", ariaLabel: dismissAriaLabel, onClick: () => fireNonCancelableEvent(onDismiss) }));
    return (React.createElement("div", { ...baseProps, ref: mergedRootRef, role: "dialog", "aria-labelledby": headerId, className: clsx(baseProps.className, styles.root, testStyles.root), onKeyDown: onKeyDown },
        React.createElement("div", { className: clsx(styles.header, headerActions && styles['header-with-actions']) },
            React.createElement("div", { className: styles['header-content'] },
                React.createElement("span", { id: headerId, className: testStyles.header }, header)),
            headerActions && (React.createElement("div", { className: clsx(styles['header-actions'], testStyles['header-actions']) }, headerActions)),
            dismissButton),
        children && React.createElement("div", { className: clsx(styles.content, testStyles.content) }, children),
        footer && React.createElement("div", { className: clsx(styles.footer, testStyles.footer) }, footer)));
}
//# sourceMappingURL=internal.js.map